import { useCallback, useEffect, useMemo, useState } from "react";

import { Button, Typography } from "@mui/material";
import dayjs from "dayjs";
import { useNavigate, useParams } from "react-router-dom";

import { useOutletApi } from "@/features/authentication";
import { DEFAULT_VALUES, OtherPaymentForm } from "@/features/other-payments";
import { getErrorMessage } from "@/features/shared";
import { CenteredLoading, PageContainer, useSnackbar } from "@/features/ui";

export const EditOtherPayment = () => {
  const { referenceNumber } = useParams();
  const navigate = useNavigate();
  const outletApi = useOutletApi();
  const { showSnackbar } = useSnackbar();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [otherPayment, setOtherPayment] = useState(null);
  const [error, setError] = useState(null);

  const loadOtherPayment = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const {
        data: { data },
      } = await outletApi.get(`other-payments/${referenceNumber}`);
      setOtherPayment(data);
    } catch (error) {
      setError(getErrorMessage(error, "Gagal memuat pembayaran"));
    } finally {
      setLoading(false);
    }
  }, [outletApi, referenceNumber]);

  useEffect(() => {
    loadOtherPayment();
  }, [loadOtherPayment]);

  const defaultValues = useMemo(() => {
    if (!otherPayment) return DEFAULT_VALUES;

    return {
      cashBankOutletChartOfAccount:
        otherPayment.cashBankOutletChartOfAccount || null,
      transactionDate: dayjs(otherPayment.transactionDate),
      checkNumber: otherPayment.checkNumber || "",
      payee: otherPayment.payee || "",
      note: otherPayment.note || "",
      otherPaymentDetails: (otherPayment.otherPaymentDetails || []).map(
        (line) => ({
          outletChartOfAccount: line.outletChartOfAccount || null,
          amount: line.amount,
          note: line.note || "",
          sourceableType: line.sourceableType || null,
          sourceableId: line.sourceableId || null,
          sourceReferenceNumber: line.sourceReferenceNumber || null,
          sourceLabel: line.sourceLabel || null,
        })
      ),
    };
  }, [otherPayment]);

  const handleSubmit = async (data) => {
    try {
      setIsSubmitting(true);
      const { outletId, ...payload } = data;

      await outletApi.put(`other-payments/${referenceNumber}`, payload, {
        headers: { "Outlet-Id": outletId },
      });

      showSnackbar("Pembayaran berhasil diperbarui", "success");
      navigate(`/general-ledger/other-payments/${referenceNumber}`);
    } catch (error) {
      showSnackbar(getErrorMessage(error, "Gagal memperbarui pembayaran"));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return <CenteredLoading sx={{ height: "100vh" }} />;
  }

  if (error) {
    return (
      <Typography variant="h6" color="error">
        {error}
      </Typography>
    );
  }

  return (
    <PageContainer
      maxWidth="xl"
      title="Ubah Pembayaran"
      breadcrumbs={[
        { title: "Pembayaran", path: "/general-ledger/other-payments" },
        {
          title: referenceNumber,
          path: `/general-ledger/other-payments/${referenceNumber}`,
        },
        { title: "Ubah" },
      ]}
    >
      <OtherPaymentForm
        defaultValues={defaultValues}
        onSubmit={handleSubmit}
        renderActionButton={() => (
          <Button
            type="submit"
            variant="contained"
            disabled={isSubmitting}
            size="large"
          >
            {isSubmitting ? "Menyimpan..." : "Simpan"}
          </Button>
        )}
      />
    </PageContainer>
  );
};
